import type { Fact } from "../../common/fact-table";
import { formatHex, toBase64 } from "./bytes";

// What the bytes under the cursor say when they are read as something else. Every one of these is the same bytes,
// which is the point: a reading that needs more bytes than the file has left is not shown rather than shown as zero,
// and `FactTable` drops a row whose value is empty, so the list is only ever the readings that exist.

export function valueReadings(bytes: Uint8Array, offset: number, little: boolean): Fact[] {
  if (offset < 0 || offset >= bytes.length) return [];
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const left = bytes.length - offset;

  return [
    { label: "Binary", value: bytes[offset].toString(2).padStart(8, "0") },
    { label: "Int8", value: String(view.getInt8(offset)) },
    { label: "UInt8", value: String(view.getUint8(offset)) },
    { label: "Int16", value: left >= 2 ? String(view.getInt16(offset, little)) : "" },
    { label: "UInt16", value: left >= 2 ? String(view.getUint16(offset, little)) : "" },
    { label: "Int32", value: left >= 4 ? String(view.getInt32(offset, little)) : "" },
    { label: "UInt32", value: left >= 4 ? String(view.getUint32(offset, little)) : "" },
    { label: "Int64", value: left >= 8 ? String(view.getBigInt64(offset, little)) : "" },
    { label: "UInt64", value: left >= 8 ? String(view.getBigUint64(offset, little)) : "" },
    { label: "Float32", value: left >= 4 ? String(view.getFloat32(offset, little)) : "" },
    { label: "Float64", value: left >= 8 ? String(view.getFloat64(offset, little)) : "" },
    { label: "Unix time", value: left >= 4 ? unixTime(view.getUint32(offset, little)) : "" },
    { label: "GUID", value: left >= 16 ? guid(bytes.subarray(offset, offset + 16), little) : "" },
  ];
}

// A run and both of its ends, which is the one thing the text column cannot answer: it draws a byte at a time, and
// UTF-8 is only a string once somebody has said where it starts and stops.
export function textReadings(bytes: Uint8Array, start: number, end: number, limit = MAX_TEXT): Fact[] {
  const run = bytes.subarray(start, Math.min(end, start + limit));
  if (run.length === 0) return [];

  return [
    { label: "UTF-8", value: printable(new TextDecoder("utf-8").decode(run)) },
    { label: "UTF-16 LE", value: run.length >= 2 ? printable(new TextDecoder("utf-16le").decode(run)) : "" },
    { label: "Hex", value: formatHex(run) },
    { label: "Base64", value: toBase64(run) },
  ];
}

// Past this a selection is being read rather than looked at, and building a megabyte-long string into a table cell
// is a page that stops responding. What is cut is said out loud beside the readings rather than left to be noticed.
export const MAX_TEXT = 1024;

// With nothing selected the card is answering "what is around the caret", which is a glance and not a reading: a
// kilobyte of a binary file decoded as text is a wall of replacement characters nobody was asking a question about.
export const PEEK = 64;

// A control code has no width and no glyph, so a decoding full of them would read as a decoding of nothing at all.
function printable(text: string): string {
  return text.replace(/[\u0000-\u001f\u007f]/g, "·");
}

function unixTime(seconds: number): string {
  const at = new Date(seconds * 1000);
  return Number.isNaN(at.getTime()) ? "" : at.toISOString().replace(".000Z", "Z");
}

// Microsoft's mixed-endian spelling when the inspector is reading little-endian, and RFC 4122's plain byte order
// when it is reading big-endian — which is the same sixteen bytes, and the two ways every format on either side of
// that divide writes them down.
function guid(bytes: Uint8Array, little: boolean): string {
  const group = (from: number, until: number) => {
    const run = bytes.subarray(from, until);
    return formatHex(little && until <= 8 ? run.slice().reverse() : run);
  };
  return [group(0, 4), group(4, 6), group(6, 8), group(8, 10), group(10, 16)].join("-");
}
